/* eslint-disable max-lines-per-function, max-len, max-lines, react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import useProjectDetailsData from '../useProjectDetailsData';
import { buildCsatHistoryTableConfig } from './CsatHistoryConfig';
import { ModalShell, fromPickerDate, toISODate, toPickerDate } from '../modalsShared';
import { Box, ClickAwayListener, Popper } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import '../../../../Components/Datepicker/CustomStyledDatePicker.scss';
import { CHOOSE_DATE } from '../../../../Constants/strings';
import CalendarIcon from '../../../../Assets/images/svg/dar_calendar_blank_gray.svg';
import EditActionIcon from '../../../../Assets/images/svg/dar_pencil.svg';
import DeleteActionIcon from '../../../../Assets/images/svg/dar_delete.svg';
import './CsatHistory.scss';
import UseAxios from '../../../../Api/http/http';
import {
  createProjectCsat,
  getCsatCustomerDetailsByProjectId,
} from '../../../../Api/request/projectDetails';
import {
  updateProjectCsat,
  deleteProjectCsat,
} from '../../../../Api/request/csatHistoryRequest';
import { useAuth } from '../../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../../Constants/modules';
import { masterProjectNameData } from '../../../../Api/request/projectOverviewMasterData';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../../Api/resources/serviceURLs';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import normalizeTableUtilityResponse from '../../../../Utils/tableUtilityResponse';


const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api/v1/csat-history`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api/v1/csat-history`;
};
const baseURL = fetchBaseURL();

const normalizeHistoryRow = (row) => {
  const csatId = row.csatId ?? row.csat_id ?? row.id ?? row.row_id ?? null;
  const projectSeq = row.projectSeqId ?? row.project_seq_id ?? row.projectId ?? row.project_id ?? '';
  const rowId = csatId ?? `not-received-${projectSeq || Math.random()}`;
  return {
    ...row,
    id: rowId,
    csatId,
    projectId: row.projectId ?? row.project_id ?? '',
    projectName: row.projectName ?? row.project_name ?? '',
    clientName: row.clientName ?? row.client_name ?? '',
    customerName: row.customerName ?? row.customer_name ?? '',
    projectVertical: row.projectVertical ?? row.project_vertical ?? '',
    projectStatus: row.projectStatus ?? row.project_status ?? '',
    projectManager: row.projectManager ?? row.project_manager ?? '',
    projectType: row.projectType ?? row.project_type ?? '',
    baselineStartDate: row.baselineStartDate ?? row.baseline_start_date ?? row.start_date ?? '',
    baselineEndDate: row.baselineEndDate ?? row.baseline_end_date ?? row.end_date ?? '',
    actualStartDate: row.actualStartDate ?? row.actual_start_date ?? '',
    replannedEndDate: row.replannedEndDate ?? row.replanned_end_date ?? '',
    activeDate: row.activeDate ?? row.active_date ?? '',
    customerId: row.customerId ?? row.customer_id ?? null,
    receivedDate: row.receivedDate ?? row.csatReceivedDate ?? row.csat_received_date ?? '',
    csatReceivedDate: row.csatReceivedDate ?? row.csat_received_date ?? row.receivedDate ?? '',
    csatNotReceivedDate: row.csatNotReceivedDate ?? row.csat_not_received_date ?? '',
    serviceQuality: Number(row.serviceQuality ?? row.qualityOfService ?? row.quality_of_service ?? 0),
    roi: Number(row.roi ?? row.returnOnInvestment ?? row.return_on_investment ?? 0),
    management: Number(row.management ?? row.managementCommunication ?? row.management_communication ?? 0),
    csatRating: Number(row.csatRating ?? row.csat_rating ?? 0),
    overall: Number(row.overall ?? row.csatRating ?? row.csat_rating ?? 0),
    feedback: row.feedback ?? row.remarks ?? '',
  };
};

const normalizeFilterValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const buildUniqueFilterOptions = (rows, selector) => Array.from(
  new Set(rows.map((row) => normalizeFilterValue(selector(row))).filter(Boolean)),
).sort((left, right) => String(left).localeCompare(String(right)));

const formatDisplayDate = (value) => {
  if (!value) {
    return '';
  }

  const text = String(value).trim();
  if (!text) {
    return '';
  }

  if (text.includes(' - ')) {
    return text
      .split(' - ')
      .map((part) => formatDisplayDate(part))
      .filter(Boolean)
      .join(' - ');
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split('-');
    const monthLabel = new Date(Number(year), Number(month) - 1, 1)
      .toLocaleString('en-US', { month: 'short' });
    return `${day}-${monthLabel}-${year}`;
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) {
    return text;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// ─── Local Custom DatePicker with performance optimization (unmount calendar when closed) ───
const LocalCustomDatePicker = ({
  value,
  onChange,
  label,
  placeholder,
  minDate,
  maxDate,
  isDisabled = false,
  error = false,
  helperText,
  displayFormat = 'DD/MM/YYYY',
  calendarSx = {},
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const calendarStyles = {
    bgcolor: '#fff',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '320px',
    minWidth: '260px',
    fontFamily: 'Mukta, sans-serif',
    overflow: 'hidden',
    boxShadow:
      '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
    '& .MuiPickersLayout-root': { maxHeight: '350px' },
    '& .MuiPickersLayout-contentWrapper': { overflowY: 'auto', maxHeight: '320px' },
    '& .MuiYearCalendar-root': { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', padding: '16px' },
    '& .MuiPickersYear-yearButton': { fontFamily: 'Mukta, sans-serif', fontSize: '14px', height: '36px', width: '100%' },
    '& .MuiTypography-root': { fontFamily: 'Mukta, sans-serif' },
    '& .MuiPickersCalendarHeader-label': { fontSize: '16px', fontWeight: 600, fontFamily: 'Mukta, sans-serif' },
    '& .MuiPickersDay-root': { fontSize: '12px', fontFamily: 'Mukta, sans-serif' },
    '& .MuiDayCalendar-weekDayLabel': { fontSize: '12px', fontWeight: 600, fontFamily: 'Mukta, sans-serif' },
    '& .MuiPickersArrowSwitcher-button': { fontFamily: 'Mukta, sans-serif' },
  };

  const mergedCalendarStyles = {
    ...calendarStyles,
    ...calendarSx,
    '& .MuiPickersCalendarHeader-label': {
      ...calendarStyles['& .MuiPickersCalendarHeader-label'],
      ...(calendarSx['& .MuiPickersCalendarHeader-label'] || {}),
    },
  };

  const displayValue = value
    ? dayjs(value).format(displayFormat)
    : placeholder || CHOOSE_DATE;

  const handleOpen = () => {
    if (!isDisabled) setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDateChange = (date) => {
    onChange(date);
    handleClose();
  };

  const buttonClassName = `
    custom-styled-date-field
    ${isDisabled ? 'is-disabled' : ''}
    ${error ? 'is-error' : ''}
  `.trim();

  return (
    <div className="custom-styled-date-container">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <button
          ref={anchorRef}
          type="button"
          className={buttonClassName}
          onClick={handleOpen}
          aria-label={label}
          disabled={isDisabled}
        >
          {label && <span className="custom-styled-date-prefix">{label}</span>}
          <span className="custom-styled-date-value">{displayValue}</span>
          <span className="custom-styled-date-icon-wrapper" aria-hidden="true">
            <img src={CalendarIcon} alt="" className="custom-styled-date-icon" />
          </span>
        </button>

        {helperText && (
          <div className={`custom-styled-date-helper ${error ? 'is-error' : ''}`}>{helperText}</div>
        )}

        {open && (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            className="custom-styled-date-popper"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Box sx={{ padding: 1, maxWidth: '100vw', overflowX: 'auto' }}>
                <DateCalendar
                  value={value}
                  onChange={handleDateChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  dayOfWeekFormatter={(day) => day.format('ddd')}
                  sx={mergedCalendarStyles}
                />
              </Box>
            </ClickAwayListener>
          </Popper>
        )}
      </LocalizationProvider>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const LocalCSATEditModal = ({
  isOpen,
  data,
  customers = [],
  isCustomersLoading = false,
  customersError = null,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const rawId = data?.csatId || data?.csat_id || data?.id || data?.row_id;
  const isEditMode = Boolean(rawId && !String(rawId).startsWith('not-received'));
  const [formData, setFormData] = useState({
    date: '',
    customerId: '',
    serviceQuality: '',
    roi: '',
    management: '',
    overall: '',
    feedback: '',
  });

  useEffect(() => {
    if (!data) return;

    const formatScoreValue = (val) => {
      if (val === null || val === undefined || val === '') return '';
      const num = Number(val);
      return Number.isNaN(num) ? '' : num.toFixed(2);
    };

    setFormData({
      date: toISODate(data.date || data.receivedDate || data.csatReceivedDate || data.csat_rec_date),
      customerId: (
        data?.customerId !== null && data?.customerId !== undefined && data?.customerId !== ''
          ? String(data.customerId)
          : data?.customer_id !== null && data?.customer_id !== undefined && data?.customer_id !== ''
            ? String(data.customer_id)
            : ''
      ),
      serviceQuality: formatScoreValue(data.serviceQuality || data.quality_of_service),
      roi: formatScoreValue(data.roi || data.return_on_investment),
      management: formatScoreValue(data.management || data.management_communication),
      overall: formatScoreValue(data.overall || data.csat_rating),
      feedback: data.feedback || data.remarks || data.otherFeedback || data.other_feedback || '',
    });
  }, [data]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      id: data?.id || data?.csatId,
      row_id: data?.row_id ?? data?.id ?? data?.csatId,
      _entity_type: data?._entity_type || 'change_request',
      category_id: data?.category_id ?? null,
      type_id: data?.type_id ?? null,
    });
  };

  if (!isOpen) return null;
  const selectedCustomerName = data?.customer || data?.customer_name || '';
  const hasMappedSelectedCustomer = customers.some(
    (customer) => String(customer.customer_id) === String(formData.customerId),
  );
  const submitButtonLabel = isEditMode ? 'Update' : 'Add';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Customer Details' : 'Add CSAT'}
      size="medium"
      modalClassName="edit-modal edit-csat-modal"
      headerAction={!isEditMode ? (
        <button
          type="button"
          className="modal-header-link"
          onClick={() => {}}
        >
          History
        </button>
      ) : null}
    >
      <form className="modal-form csat-compact-form" onSubmit={handleSubmit}>
        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">CSAT Received Date</label>
            <LocalCustomDatePicker
              label=""
              placeholder="Choose date"
              value={toPickerDate(formData.date)}
              onChange={(date) => setFormData({ ...formData, date: fromPickerDate(date) })}
              displayFormat="DD-MMM-YYYY"
              maxDate={dayjs()}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="csat-customer-name">Customer Name</label>
          <select
            id="csat-customer-name"
            value={formData.customerId}
            onChange={(event) => setFormData({
              ...formData,
              customerId: event.target.value,
            })}
            disabled={Boolean(customersError)}
          >
            <option value="" disabled>{isCustomersLoading ? 'Loading customers...' : 'Select customer'}</option>
            {!hasMappedSelectedCustomer && formData.customerId && selectedCustomerName ? (
              <option value={formData.customerId}>{selectedCustomerName}</option>
            ) : null}
            {customers.map((customer) => (
              <option key={customer.customer_id} value={customer.customer_id}>
                {customer.customer_name}
              </option>
            ))}
          </select>
          {customersError ? <small className="field-error">Unable to load customers</small> : null}
        </div>

        <div className="form-group">
          <label htmlFor="csat-other-feedback">Other Feedback</label>
          <textarea
            id="csat-other-feedback"
            value={formData.feedback}
            onChange={(event) => setFormData({ ...formData, feedback: event.target.value })}
            placeholder="Enter feedback"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-service-quality">How happy are you with the quality of service from Niveus?</label>
          <input
            id="csat-service-quality"
            className="csat-score-input"
            type="number"
            step="0.01"
            min="0"
            max="5"
            value={formData.serviceQuality}
            onChange={(event) => setFormData({ ...formData, serviceQuality: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-roi-score">How happy are you with the Return-On-Investment on your project?</label>
          <input
            id="csat-roi-score"
            className="csat-score-input"
            type="number"
            step="0.01"
            min="0"
            max="5"
            value={formData.roi}
            onChange={(event) => setFormData({ ...formData, roi: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-management-score">
            How happy are you with the project management & communication on your project?
          </label>
          <input
            id="csat-management-score"
            className="csat-score-input"
            type="number"
            step="0.01"
            min="0"
            max="5"
            value={formData.management}
            onChange={(event) => setFormData({ ...formData, management: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-overall-score">How would you like to rate your overall experience?</label>
          <input
            id="csat-overall-score"
            className="csat-score-input"
            type="number"
            step="0.01"
            min="0"
            max="5"
            value={formData.overall}
            onChange={(event) => setFormData({ ...formData, overall: event.target.value })}
            required
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {submitButtonLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ─── helpers (module-level, no React deps) ────────────────────────────────
const toNumericProjectId = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const getProjectNameMasterRows = (response = {}) => (
  response?.data?.data?.rows
  || response?.data?.rows
  || response?.data?.data?.masterProjectNameDetails
  || response?.data?.masterProjectNameDetails
  || response?.data?.data?.masterProjectNames
  || response?.data?.masterProjectNames
  || response?.data?.data?.projectNames
  || response?.data?.projectNames
  || response?.data?.data?.masterProjectList
  || response?.data?.masterProjectList
  || []
);

const normalizeProjectMasterOptions = (rows = []) => (
  (Array.isArray(rows) ? rows : []).map((item) => {
    const name = String(
      item?.masterProjectName
      ?? item?.master_project_name
      ?? item?.projectName
      ?? item?.project_name
      ?? item?.name
      ?? item?.label
      ?? '',
    ).trim();
    const projectId = toNumericProjectId(
      item?.masterProjectId
      ?? item?.master_project_id
      ?? item?.projectId
      ?? item?.project_id
      ?? item?.id
      ?? item?.value,
    );
    if (!name || !projectId) return null;
    return { projectId, name };
  }).filter(Boolean)
);


// ─── CSAT-specific delete confirmation modal (BCR-style, no ModalShell overhead) ───
const CsatDeleteModal = ({ isOpen, onClose, onConfirm, isSubmitting = false }) => {
  if (!isOpen) return null;
  return (
    <div
      className="csat-delete-backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="csat-delete-modal">
        <div className="csat-delete-modal__header">Delete CSAT</div>
        <div className="csat-delete-modal__body">
          <div className="csat-delete-modal__warning" aria-hidden="true" />
          <div className="csat-delete-modal__message">Are you sure you want to delete this CSAT rating?</div>
        </div>
        <div className="csat-delete-modal__footer">
          <button
            type="button"
            className="csat-delete-modal__btn csat-delete-modal__btn--ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="csat-delete-modal__btn csat-delete-modal__btn--primary"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};
// ──────────────────────────────────────────────────────────────────────────

const CsatHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const projectIdParam = useMemo(() => params.get('projectId') || location.state?.projectId || '', [params, location.state]);
  const projectNameParam = useMemo(() => params.get('projectName') || location.state?.projectName || '', [params, location.state]);
  const isMisRoute = !projectIdParam;

  const {
    refetchProjectData,
    csatCustomers,
  } = useProjectDetailsData({
    queryKey: 'projectId',
    fallbackKeys: ['projectSeqId', 'id'],
  });

  const [activeEditProjectCustomers, setActiveEditProjectCustomers] = useState([]);
  const [isModalCustomersLoading, setIsModalCustomersLoading] = useState(false);
  const permissions = useMemo(() => userProfile?.permissions || {}, [userProfile]);

  const hasPermissionAction = useMemo(() => {
    return (moduleName, action) => {
      const actions = Array.isArray(permissions?.[moduleName]) ? permissions[moduleName] : [];
      return actions.includes(action);
    };
  }, [permissions]);

  const canViewDeliveryCsat = useMemo(() => hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'read'), [hasPermissionAction]);
  const canViewMisCsat = useMemo(() => hasPermissionAction(MODULE_NAMES.MIS_CSAT_HISTORY, 'read'), [hasPermissionAction]);
  const canViewCsat = useMemo(() => canViewDeliveryCsat || canViewMisCsat, [canViewDeliveryCsat, canViewMisCsat]);

  const canUpdateCsat = useMemo(() => hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'update') || hasPermissionAction(MODULE_NAMES.MIS_CSAT_HISTORY, 'update'), [hasPermissionAction]);
  const canDeleteCsat = useMemo(() => hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'delete') || hasPermissionAction(MODULE_NAMES.MIS_CSAT_HISTORY, 'delete'), [hasPermissionAction]);

  const canViewCustomerDetails = useMemo(() => {
    const superAdminFlag = userProfile?.superAdmin ?? userProfile?.super_admin;
    const isSuperAdmin = Number(superAdminFlag) === 1 || superAdminFlag === true;
    return isSuperAdmin || hasPermissionAction(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'read') || canViewCsat;
  }, [userProfile, hasPermissionAction, canViewCsat]);

  // active project – may change when user picks a different project in the filter
  const [activeProjectId, setActiveProjectId] = useState(() => projectIdParam);
  const initialProjectIdRef = useRef(projectIdParam || location.state?.projectId || '');
  // Ref mirror of activeProjectId so the plain (non-useCallback) handleFilteredApiRequest
  // always reads the latest value without closing over stale state.
  const activeProjectIdRef = useRef(projectIdParam);
  const isInitialFilterApplied = useRef(false);
  // Keep the ref in sync whenever state changes
  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);
  const projectId = activeProjectId || projectIdParam;
  const projectMasterOptionsRef = useRef([]);
  const [projectMasterOptions, setProjectMasterOptions] = useState([]);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [tableRefreshNonce, setTableRefreshNonce] = useState(0);
  const refreshTable = useCallback(() => {
    currentPageRef.current = 1;
    setTableRefreshNonce((current) => current + 1);
  }, []);

  // Track page internally using a ref so any config rebuild preserves the real
  // current page instead of snapping back to 1 and triggering an infinite re-fetch
  // (same pattern as EmployeeCostDetailsMain).
  const currentPageRef = useRef(1);
  // lastFiltersRef stores the JSON-serialised filter array from the most-recent
  // request so we can detect actual changes without comparing object trees.
  const lastFiltersJsonRef = useRef(null);
  const [__pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const handlePaginationChange = useCallback((newPagination) => {
    currentPageRef.current = Number(newPagination.page) || 1;
    setPagination(newPagination);
  }, []);

  const [modalState, setModalState] = useState({
    csatEdit: { isOpen: false, data: null },
    csatDelete: { isOpen: false, data: null },
  });
  const moreMenuRef = useRef(null);
  // Tracks in-flight customer fetch so we can abort it if Cancel is pressed
  const customerFetchAbortRef = useRef(null);
  // Cache project customers list to avoid redundant API requests and speed up modal opening
  const customerCacheRef = useRef({});

  // ── Stable close handlers (useCallback avoids recreating on every render) ──
  const handleEditClose = useCallback(() => {
    // Abort any pending customer fetch so it can't update state after modal closes
    if (customerFetchAbortRef.current) {
      customerFetchAbortRef.current.abort();
      customerFetchAbortRef.current = null;
    }
    setModalState((prev) => ({ ...prev, csatEdit: { isOpen: false, data: null } }));
    setActiveEditProjectCustomers([]);
    setIsModalCustomersLoading(false);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, csatDelete: { isOpen: false, data: null } }));
  }, []);

  // ── Open edit modal + fetch customers (with abort-on-cancel support) ────────
  const openEditModal = useCallback(async (row) => {
    // Abort any previous in-flight fetch
    if (customerFetchAbortRef.current) {
      customerFetchAbortRef.current.abort();
      customerFetchAbortRef.current = null;
    }

    const projId = row.project_seq_id || row.projectId || row.project_id;
    if (!projId) {
      setActiveEditProjectCustomers([]);
      setModalState((prev) => ({ ...prev, csatEdit: { isOpen: true, data: row } }));
      return;
    }

    // Check if we have it cached locally
    if (customerCacheRef.current[projId]) {
      setActiveEditProjectCustomers(customerCacheRef.current[projId]);
      setIsModalCustomersLoading(false);
      setModalState((prev) => ({ ...prev, csatEdit: { isOpen: true, data: row } }));
      return;
    }

    // Check if it matches the current loaded project's customers
    const currentProjId = activeProjectId || projectIdParam;
    if (String(projId) === String(currentProjId) && csatCustomers && csatCustomers.length > 0) {
      setActiveEditProjectCustomers(csatCustomers);
      setIsModalCustomersLoading(false);
      // Cache it
      customerCacheRef.current[projId] = csatCustomers;
      setModalState((prev) => ({ ...prev, csatEdit: { isOpen: true, data: row } }));
      return;
    }

    // Otherwise, fetch asynchronously
    setActiveEditProjectCustomers([]);
    setModalState((prev) => ({ ...prev, csatEdit: { isOpen: true, data: row } }));

    const controller = new AbortController();
    customerFetchAbortRef.current = controller;
    setIsModalCustomersLoading(true);

    try {
      const response = await UseAxios(getCsatCustomerDetailsByProjectId(projId));
      // If cancel was pressed while we were waiting, do nothing
      if (controller.signal.aborted) return;
      const responseData = response?.data;
      const rawRows = Array.isArray(responseData?.csatCustomerDetailsList)
        ? responseData.csatCustomerDetailsList
        : Array.isArray(responseData?.data?.csatCustomerDetailsList)
          ? responseData.data.csatCustomerDetailsList
          : Array.isArray(responseData?.customers)
            ? responseData.customers
            : Array.isArray(responseData?.data?.customers)
              ? responseData.data.customers
              : Array.isArray(responseData)
                ? responseData
                : [];
      const normalized = rawRows
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          customer_id: item.customer_id ?? item.id ?? item.customerId,
          customer_name: item.customer_name ?? item.name ?? item.customerName,
        }))
        .filter((item) => item.customer_id !== null && item.customer_id !== undefined && item.customer_name);

      // Save in cache
      customerCacheRef.current[projId] = normalized;

      setActiveEditProjectCustomers(normalized);
    } catch {
      if (!controller.signal.aborted) {
        setActiveEditProjectCustomers([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsModalCustomersLoading(false);
        customerFetchAbortRef.current = null;
      }
    }
  }, [activeProjectId, projectIdParam, csatCustomers]);

  const submitCSATEdit = useCallback(async (formData) => {
    if (isActionInProgress) return;
    const rawRowId = modalState.csatEdit.data?.csatId
      || modalState.csatEdit.data?.id
      || modalState.csatEdit.data?.row_id;
    const csatRowId = (rawRowId && !String(rawRowId).startsWith('not-received')) ? rawRowId : null;
    const isEditMode = Boolean(csatRowId);
    if (!formData?.date) {
      toast.error('Please select CSAT received date.');
      return;
    }
    if (dayjs(formData.date).isAfter(dayjs(), 'day')) {
      toast.error('CSAT received date cannot be in the future.');
      return;
    }
    const customerId = formData?.customerId ? Number(formData.customerId) : null;
    if (!customerId) {
      toast.error('Please select a valid customer.');
      return;
    }
    if (!String(formData?.feedback || '').trim()) {
      toast.error('Please enter other feedback.');
      return;
    }

    setIsActionInProgress(true);
    try {
      const requestPayload = {
        project_id: modalState.csatEdit.data?.projectSeqId
          || modalState.csatEdit.data?.projectId
          || modalState.csatEdit.data?.project_id
          || projectId,
        customer_id: customerId,
        csat_rec_date: formData.date,
        quality_of_service: Number(formData.serviceQuality),
        return_on_investment: Number(formData.roi),
        management_communication: Number(formData.management),
        csat_rating: Number(formData.overall),
        remarks: String(formData.feedback || '').trim(),
        employeeId: userProfile?.employeeId,
        employeeName: userProfile?.name,
        email: userProfile?.email,
      };
      const response = isEditMode
        ? await UseAxios(updateProjectCsat(csatRowId, requestPayload))
        : await UseAxios(createProjectCsat(requestPayload));

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(isEditMode ? 'CSAT updated successfully.' : 'CSAT added successfully.');
        setModalState((prev) => ({
          ...prev,
          csatEdit: { isOpen: false, data: null },
        }));
        refreshTable();
        await refetchProjectData();
      } else {
        throw new Error(isEditMode ? 'Failed to update CSAT.' : 'Failed to add CSAT.');
      }
    } catch (err) {
      toast.error(err?.message || (isEditMode ? 'Failed to update CSAT.' : 'Failed to add CSAT.'));
    } finally {
      setIsActionInProgress(false);
    }
  }, [isActionInProgress, modalState.csatEdit.data, projectId, refetchProjectData, refreshTable, userProfile]);

  const confirmCSATDelete = useCallback(async () => {
    const targetRow = modalState.csatDelete.data;
    const csatRowId = targetRow?.csatId || targetRow?.id || targetRow?.row_id;
    if (isActionInProgress) return;
    if (!csatRowId) {
      toast.error('Missing required fields. Please provide CSAT row id.');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectCsat(csatRowId, {
        employeeId: userProfile?.employeeId,
        employeeName: userProfile?.name,
        email: userProfile?.email,
      }));
      if (response && response.status === 200) {
        toast.success('CSAT deleted successfully.');
        setModalState((prev) => ({
          ...prev,
          csatDelete: { isOpen: false, data: null },
        }));
        refreshTable();
        await refetchProjectData();
      } else {
        throw new Error('Failed to delete CSAT.');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to delete CSAT.');
    } finally {
      setIsActionInProgress(false);
    }
  }, [isActionInProgress, modalState.csatDelete.data, refetchProjectData, refreshTable, userProfile]);

  // ────────────────────────────────────────────────────────────────────────────

  const [projectDetailsFilterOptions, setProjectDetailsFilterOptions] = useState({});
  const tableStateRef = useRef(null);

  // keep activeProjectId in sync when the URL param changes (e.g. back button)
  useEffect(() => {
    setActiveProjectId(projectIdParam);
    refetchProjectData();
    lastFiltersJsonRef.current = null;
    isInitialFilterApplied.current = false;
    setTableRefreshNonce((current) => current + 1);
  }, [projectIdParam, refetchProjectData]);

  // load project master names once (for project-name → project-id lookup)
  const loadProjectMasterOptions = useCallback(async () => {
    if (projectMasterOptionsRef.current.length > 0) {
      return projectMasterOptionsRef.current;
    }
    try {
      const response = await UseAxios(masterProjectNameData());
      const options = normalizeProjectMasterOptions(getProjectNameMasterRows(response));
      if (options.length > 0) {
        projectMasterOptionsRef.current = options;
        setProjectMasterOptions(options);
      }
    } catch {
      // non-critical – fall back to no lookup
    }
    return projectMasterOptionsRef.current;
  }, []);

  useEffect(() => {
    loadProjectMasterOptions();
  }, [loadProjectMasterOptions]);

  const resolvedProjectName = useMemo(() => {
    if (projectNameParam) return projectNameParam;
    const rowWithProjectName = historyRows.find(
      (row) => row.projectId && String(row.projectId) === String(projectId) && row.projectName,
    );
    if (rowWithProjectName) return rowWithProjectName.projectName;
    if (Array.isArray(projectDetailsFilterOptions?.projectList)) {
      const match = projectDetailsFilterOptions.projectList.find(
        (opt) => String(opt.projectId) === String(projectId),
      );
      if (match) return match.projectName;
    }
    const matchedOption = projectMasterOptions.find(
      (opt) => String(opt.projectId) === String(projectId),
    );
    if (matchedOption) return matchedOption.name;
    return '';
  }, [projectNameParam, historyRows, projectId, projectMasterOptions, projectDetailsFilterOptions]);

  useEffect(() => {
    const targetId = projectIdParam || location.state?.projectId;
    if (!targetId || isInitialFilterApplied.current) return;

    if (!resolvedProjectName) return;

    const timer = setTimeout(() => {
      const tableApi = typeof tableStateRef.current === 'function' ? tableStateRef.current() : tableStateRef.current;
      if (!tableApi) return;

      const existingFilters = tableApi.filters || [];
      const updatedFilters = [
        ...existingFilters.filter((filterItem) => filterItem.field !== 'projectName'),
        {
          field: 'projectName',
          selectedValue: resolvedProjectName,
          selectedValues: [resolvedProjectName],
        },
      ];

      tableApi.setFilters?.(updatedFilters);
      tableApi.fetchTableData?.(
        updatedFilters,
        { limit: 10, page: 1 },
        '',
      );

      isInitialFilterApplied.current = true;

      // ── Mark the filter button purple immediately after URL-param filter is applied ──
      // A short extra delay lets the DOM settle after setFilters/fetchTableData.
      setTimeout(() => {
        const filterBtn = document.querySelector('.table-utility-filter-btn');
        if (filterBtn) filterBtn.classList.add('active');
      }, 100);
    }, 500);

    return () => clearTimeout(timer);
  }, [projectIdParam, location.state?.projectId, resolvedProjectName]);

  const projectName = resolvedProjectName;
  const projectVertical = '-';

  const filterOptions = useMemo(() => ({
    projectId: buildUniqueFilterOptions(historyRows, (row) => row.projectId),
    projectName: projectDetailsFilterOptions.projectName || [],
    clientName: projectDetailsFilterOptions.clientName || [],
    projectVertical: projectDetailsFilterOptions.projectVertical || [],
    projectType: projectDetailsFilterOptions.projectType || [],
    projectStatus: projectDetailsFilterOptions.projectStatus || [],
    projectManager: projectDetailsFilterOptions.projectManager || [],
    baselineStartDate: projectDetailsFilterOptions.baselineStartDate || [],
    baselineEndDate: projectDetailsFilterOptions.baselineEndDate || [],
    actualStartDate: projectDetailsFilterOptions.actualStartDate || [],
    replannedEndDate: projectDetailsFilterOptions.replannedEndDate || [],
    activeDate: projectDetailsFilterOptions.activeDate || [],
    receivedDate: buildUniqueFilterOptions(historyRows, (row) => row.receivedDate),
    csatNotReceivedDate: buildUniqueFilterOptions(historyRows, (row) => row.csatNotReceivedDate),
  }), [projectDetailsFilterOptions, historyRows]);

  const csatHistoryConfig = useMemo(
    () => buildCsatHistoryTableConfig(filterOptions, resolvedProjectName, canUpdateCsat || canDeleteCsat, projectId),
    [filterOptions, resolvedProjectName, canUpdateCsat, canDeleteCsat, projectId],
  );

  // Plain function — NOT useCallback — so it is never recreated and never
  // becomes a dep that rebuilds `configuration` (same as EmployeeCostDetailsMain).
  // Uses lastFiltersJsonRef (JSON string) for O(1) change detection.
  const handleFilteredApiRequest = (requestData) => {
    let activeFilters = requestData.filter || [];

    const targetProjId = projectIdParam || initialProjectIdRef.current || location.state?.projectId;

    // ── Project-name filter handling ──────────────────────────────────────────
    const projectFilterItem = activeFilters.find((fi) => fi && fi.field === 'projectName');
    const hasProjectNameSelected = projectFilterItem && (
      (projectFilterItem.selectedValue != null && projectFilterItem.selectedValue !== '')
      || (Array.isArray(projectFilterItem.selectedValues) && projectFilterItem.selectedValues.length > 0)
    );

    const extractStringValue = (val) => {
      if (!val) return '';
      if (typeof val === 'object') return String(val.label ?? val.name ?? val.value ?? '').trim();
      return String(val).trim();
    };

    let selectedProjName = '';
    if (projectFilterItem) {
      selectedProjName = extractStringValue(
        projectFilterItem.selectedValue
        || (Array.isArray(projectFilterItem.selectedValues) && projectFilterItem.selectedValues[0])
        || projectFilterItem.value,
      );
    }

    // Check if non-project filters changed (reset project filter when they do)
    const nonProjectFilters = activeFilters.filter((fi) => fi && fi.field && fi.field !== 'projectName' && fi.field !== 'projectId');
    const nonProjectJson = JSON.stringify(nonProjectFilters);
    let otherFiltersChanged = false;
    if (lastFiltersJsonRef.current !== null) {
      try {
        const prev = JSON.parse(lastFiltersJsonRef.current);
        const prevNonProject = prev.filter((fi) => fi && fi.field && fi.field !== 'projectName' && fi.field !== 'projectId');
        otherFiltersChanged = JSON.stringify(prevNonProject) !== nonProjectJson;
      } catch (__err) { /* ignore */ }
    }

    if (otherFiltersChanged && hasProjectNameSelected && projectFilterItem) {
      projectFilterItem.selectedValue = null;
      projectFilterItem.selectedValues = [];
      projectFilterItem.value = null;
      selectedProjName = '';
    }

    // ── Resolve projectId from the project-name filter ────────────────────────
    let nextProjectId = activeProjectIdRef.current || targetProjId || '';
    if (hasProjectNameSelected && selectedProjName) {
      const option = projectMasterOptionsRef.current.find(
        (opt) => String(opt.name || opt.projectName || '').trim()
          .toLowerCase() === selectedProjName.trim().toLowerCase(),
      );
      if (option) {
        nextProjectId = String(option.projectId);
      } else if (
        (resolvedProjectName && selectedProjName.trim().toLowerCase() === resolvedProjectName.trim().toLowerCase())
        || (projectNameParam && selectedProjName.trim().toLowerCase() === projectNameParam.trim().toLowerCase())
      ) {
        nextProjectId = activeProjectIdRef.current || targetProjId || '';
      }
    } else if (projectFilterItem && !hasProjectNameSelected && isInitialFilterApplied.current) {
      const hasExplicitProjFilter = activeFilters.some((filterItem) => filterItem.field === 'projectId');
      if (hasExplicitProjFilter) {
        nextProjectId = '';
      }
    }

    if (nextProjectId) {
      const trimmedId = String(nextProjectId).trim();
      const existingProjFilterIndex = activeFilters.findIndex((filterItem) => filterItem.field === 'projectId');
      const projFilterObj = {
        field: 'projectId',
        label: 'Project ID',
        template: 'singleSelect',
        selectedLabel: trimmedId,
        selectedLabels: [trimmedId],
        selectedValue: { id: trimmedId, name: trimmedId, label: trimmedId, value: trimmedId },
        selectedValues: [{ id: trimmedId, name: trimmedId, label: trimmedId, value: trimmedId }],
        value: trimmedId,
      };
      if (existingProjFilterIndex >= 0) {
        activeFilters[existingProjFilterIndex] = projFilterObj;
      } else {
        activeFilters = [...activeFilters, projFilterObj];
      }
      requestData.filter = activeFilters;
    } else {
      activeFilters = activeFilters.filter((filterItem) => filterItem.field !== 'projectId');
      requestData.filter = activeFilters;
    }

    isInitialFilterApplied.current = true;

    const currentSearchParams = new URLSearchParams(window.location.search);
    const hasProjectNameInUrl = currentSearchParams.has('projectName');

    if (nextProjectId !== activeProjectIdRef.current || hasProjectNameInUrl) {
      activeProjectIdRef.current = nextProjectId;
      setActiveProjectId(nextProjectId);
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete('projectName');
      if (nextProjectId) {
        nextParams.set('projectId', nextProjectId);
      } else {
        nextParams.delete('projectId');
      }
      const nextUrl = nextParams.toString()
        ? `${window.location.pathname}?${nextParams.toString()}`
        : window.location.pathname;
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(window.history.state, '', nextUrl);
      }
      navigate(nextUrl, { replace: true });
    }

    // ── Page-reset on filter change (JSON string comparison, O(1)) ────────────
    const currentFiltersJson = JSON.stringify(activeFilters);
    const filterChanged = lastFiltersJsonRef.current !== null
      && lastFiltersJsonRef.current !== currentFiltersJson;
    lastFiltersJsonRef.current = currentFiltersJson;

    let page = Number(requestData?.pagination?.page) || 1;
    const limit = Number(requestData?.pagination?.limit) || 10;

    if (filterChanged) {
      page = 1;
      // Keep the ref in sync so any subsequent config rebuild uses page 1.
      // Do NOT call setPagination here – that would rebuild `configuration`
      // and cause TableUtility to re-initialise, wiping the applied filter.
    }
    currentPageRef.current = page;

    // Mutate requestData in place AND return the enriched copy
    requestData.page = page;
    requestData.limit = limit;
    if (requestData.pagination) {
      requestData.pagination.page = page;
      requestData.pagination.limit = limit;
    }

    // ── Sync the filter button active (purple) class ─────────────────────────
    const isFilterItemActive = (fi) => {
      if (!fi || !fi.field) return false;
      if (Array.isArray(fi.selectedValues) && fi.selectedValues.length > 0) return true;
      if (fi.selectedValue != null && fi.selectedValue !== '') return true;
      if (fi.value != null && fi.value !== '') return true;
      return false;
    };
    const hasActiveFilter = activeFilters.some(isFilterItemActive);
    const filterBtn = document.querySelector('.table-utility-filter-btn');
    if (filterBtn) filterBtn.classList.toggle('active', hasActiveFilter);

    return {
      ...requestData,
      page,
      limit,
      filter: activeFilters,
      pagination: { page, limit },
      ...(nextProjectId ? { projectId: nextProjectId } : {}),
      payload: {
        userInfo: {
          id: userProfile?.id,
          emailId: userProfile?.email,
          employeeId: userProfile?.employeeId,
          name: userProfile?.name,
          permissions: userProfile?.permissions,
          roleDetails: userProfile?.roleDetails,
        },
        ...(nextProjectId ? { projectId: nextProjectId } : {}),
        additionalPayloadData: {
          ...(nextProjectId ? { projectId: nextProjectId } : {}),
        },
      },
    };
  };

  const handleFilteredApiResponse = useCallback((response) => {
    if (response) {
      const totalCount = response.pagination?.totalCount
        ?? response.data?.pagination?.totalCount
        ?? response.data?.data?.pagination?.totalCount
        ?? response.meta?.total
        ?? 0;

      if (response.pagination) {
        response.pagination.total = totalCount;
        response.pagination.count = totalCount;
      }
      if (response.data) {
        const rawTableData = response.data.tableData
          || response.data.data?.tableData
          || response.tableData
          || response.data.data
          || [];

        const normalizedRows = (Array.isArray(rawTableData) ? rawTableData : []).map(normalizeHistoryRow);

        const filterOpts = response.filterOptions
          || response.data.filterOptions
          || response.data.data?.filterOptions
          || {};

        const freshFilterOptions = {
          projectId: filterOpts.projectId || buildUniqueFilterOptions(normalizedRows, (row) => row.projectId),
          projectName: filterOpts.projectName || [],
          clientName: filterOpts.clientName || [],
          projectVertical: filterOpts.projectVertical || [],
          projectType: filterOpts.projectType || [],
          projectStatus: filterOpts.projectStatus || [],
          projectManager: filterOpts.projectManager || [],
          baselineStartDate: filterOpts.baselineStartDate || [],
          baselineEndDate: filterOpts.baselineEndDate || [],
          actualStartDate: filterOpts.actualStartDate || [],
          replannedEndDate: filterOpts.replannedEndDate || [],
          activeDate: filterOpts.activeDate || [],
          receivedDate: buildUniqueFilterOptions(normalizedRows, (row) => row.receivedDate),
          csatNotReceivedDate: buildUniqueFilterOptions(normalizedRows, (row) => row.csatNotReceivedDate),
        };

        const freshTableConfig = buildCsatHistoryTableConfig(freshFilterOptions, resolvedProjectName, canUpdateCsat || canDeleteCsat, projectId);
        response.data.tableConfiguration = freshTableConfig;

        if (response.data.pagination) {
          response.data.pagination.total = totalCount;
          response.data.pagination.count = totalCount;
        }
        if (response.data.data && response.data.data.pagination) {
          response.data.data.pagination.total = totalCount;
          response.data.data.pagination.count = totalCount;
        }
      }
    }

    const normalizedResponse = normalizeTableUtilityResponse(response);
    const rawData = normalizedResponse?.data && typeof normalizedResponse.data === 'object' ? normalizedResponse.data : {};

    const paginationData = normalizedResponse.pagination
      || normalizedResponse.data?.pagination
      || normalizedResponse.data?.paginationConfig
      || normalizedResponse.data?.data?.pagination
      || normalizedResponse.data?.data?.paginationConfig
      || normalizedResponse.meta;

    setPagination((prev) => {
      const nextPage = Number(paginationData?.page ?? prev.page);
      const nextLimit = Number(paginationData?.limit ?? prev.limit);
      currentPageRef.current = nextPage;
      return {
        ...prev,
        page: nextPage,
        limit: nextLimit,
        total: Number(paginationData?.total ?? paginationData?.totalCount ?? prev.total),
      };
    });
    const apiRows = Array.isArray(rawData.tableData)
      ? rawData.tableData
      : Array.isArray(rawData.rows)
        ? rawData.rows
        : [];

    const filterOpts = normalizedResponse?.data?.filterOptions || response?.filterOptions || {};
    if (filterOpts && typeof filterOpts === 'object' && Object.keys(filterOpts).length > 0) {
      setProjectDetailsFilterOptions(filterOpts);
    }

    const enrichedRows = apiRows.map((row) => {
      const normalizedRow = normalizeHistoryRow(row);
      const rowProjectId = normalizedRow.projectId || projectId;
      const rowProjectName = normalizedRow.projectName || projectName;

      return {
        ...normalizedRow,
        projectId: rowProjectId,
        projectName: rowProjectName,
        clientName: normalizedRow.clientName,
        customerName: normalizedRow.customerName,
        projectVertical: normalizedRow.projectVertical || projectVertical,
        baselineStartDate: formatDisplayDate(normalizedRow.baselineStartDate),
        baselineEndDate: formatDisplayDate(normalizedRow.baselineEndDate),
        actualStartDate: formatDisplayDate(normalizedRow.actualStartDate),
        replannedEndDate: formatDisplayDate(normalizedRow.replannedEndDate),
        receivedDate: formatDisplayDate(normalizedRow.receivedDate),
        csatNotReceivedDate: formatDisplayDate(normalizedRow.csatNotReceivedDate),
        activeDate: formatDisplayDate(normalizedRow.activeDate),
        serviceQuality: typeof normalizedRow.serviceQuality === 'number' ? normalizedRow.serviceQuality.toFixed(2) : '0.00',
        roi: typeof normalizedRow.roi === 'number' ? normalizedRow.roi.toFixed(2) : '0.00',
        management: typeof normalizedRow.management === 'number' ? normalizedRow.management.toFixed(2) : '0.00',
        action: (
          <div className="csat-action-cell" onClick={(event) => event.stopPropagation()}>
            {canUpdateCsat && (
              <button
                type="button"
                className="csat-action-btn edit-btn"
                title={normalizedRow.csatId ? 'Edit CSAT' : 'Add CSAT'}
                onClick={() => openEditModal(normalizedRow)}
              >
                <img src={EditActionIcon} alt={normalizedRow.csatId ? 'Edit' : 'Add'} />
              </button>
            )}
            {canDeleteCsat && normalizedRow.csatId && (
              <button
                type="button"
                className="csat-action-btn delete-btn"
                title="Delete CSAT"
                onClick={() => setModalState((prev) => ({ ...prev, csatDelete: { isOpen: true, data: normalizedRow } }))}
              >
                <img src={DeleteActionIcon} alt="Delete" />
              </button>
            )}
          </div>
        ),
      };
    });

    setHistoryRows(apiRows.map(normalizeHistoryRow));

    const finalTotalCount = Number(paginationData?.total ?? paginationData?.totalCount ?? 0);
    response.meta = {
      ...(response.meta || {}),
      page: Number(paginationData?.page ?? 1),
      limit: Number(paginationData?.limit ?? 10),
      total: finalTotalCount,
    };

    response.data = {
      ...rawData,
      tableData: enrichedRows,
      meta: response.meta,
    };

    return response;
  }, [projectId, projectName, projectVertical, canUpdateCsat, canDeleteCsat, openEditModal]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const cells = document.querySelectorAll('td.col-project-name, td.col-client-name');
      cells.forEach((cell) => {
        const textSpan = cell.querySelector('span, div') || cell;
        const text = textSpan ? textSpan.textContent : cell.textContent;
        if (text && cell.getAttribute('title') !== text) {
          cell.setAttribute('title', text);
        }
      });

      // Column `className`/`bodyClassName` set via config is not forwarded to the
      // rendered <td> by the dynamic-table package, so the "Other Feedback" column
      // is located by its header text instead (same technique used for the
      // Sr. Manager/Project Manager column in ProjectWiseExpenseReport).
      const container = document.querySelector('.csat-history-table-container');
      if (!container) return;

      const tables = container.querySelectorAll('table, .p-datatable-table');
      tables.forEach((table) => {
        const headerCells = table.querySelectorAll('.p-datatable-thead tr th, thead tr th');
        const feedbackHeader = Array.from(headerCells).find(
          (hdr) => (hdr.textContent || '').trim().toLowerCase() === 'other feedback',
        );
        if (!feedbackHeader) return;

        const colIndex = Array.from(feedbackHeader.parentElement.children).indexOf(feedbackHeader) + 1;
        const bodyCells = table.querySelectorAll(
          `.p-datatable-tbody tr td:nth-child(${colIndex}), tbody tr td:nth-child(${colIndex})`,
        );

        bodyCells.forEach((cell) => {
          cell.style.maxWidth = '250px';
          cell.style.overflow = 'hidden';
          cell.style.textOverflow = 'ellipsis';
          cell.style.whiteSpace = 'nowrap';

          const content = cell.querySelector('div') || cell;
          const fullText = (content.textContent || '').trim();
          content.style.overflow = 'hidden';
          content.style.textOverflow = 'ellipsis';
          content.style.whiteSpace = 'nowrap';

          if (fullText) {
            cell.setAttribute('title', fullText);
            content.setAttribute('title', fullText);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const toolbarMenuItems = useMemo(() => ([
    {
      label: 'CSAT Customer Details',
      command: () => navigate(`/projects/csat-customer-details${projectId ? `?projectId=${projectId}` : ''}`),
    },
  ]), [navigate, projectId]);

  const toolbarAdditionalElements = useMemo(() => {
    if (!canUpdateCsat && !canDeleteCsat && !canViewCustomerDetails) return [];
    return [
      <React.Fragment key="csat-toolbar-actions">
        <Menu
          ref={moreMenuRef}
          model={toolbarMenuItems}
          popup
          appendTo={document.body}
          className="csat-history-more-menu"
        />
        <Button
          type="button"
          icon="pi pi-ellipsis-v"
          className="p-button-text csat-history-more-btn"
          onClick={(event) => moreMenuRef.current?.toggle(event)}
          aria-label="More"
          tooltip="More options"
          tooltipOptions={{ position: 'top' }}
        />
      </React.Fragment>,
    ];
  }, [toolbarMenuItems, canUpdateCsat, canDeleteCsat, canViewCustomerDetails]);

  // ── Stable configuration (EmployeeCostDetailsMain pattern) ──────────────────
  // Only depends on stable values so that API responses / state updates never
  // rebuild this object and trigger a TableUtility re-initialisation that would
  // wipe the user’s applied filter state.
  //
  // Key rules (same as EmployeeCostDetailsMain):
  //   • paginationConfig.page  → currentPageRef.current  (ref, not state)
  //   • paginationConfig.total → 0  (TableUtility reads real total from the response)
  //   • tableConfig             → csatHistoryConfig initial value captured in a ref
  //   • Deps                   → only stableUserProfile + toolbarAdditionalElements
  // eslint-disable-next-line max-lines
  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        id: userProfile?.id,
        emailId: userProfile?.email,
        permissions: userProfile?.permissions,
        roleDetails: userProfile?.roleDetails,
        employeeId: userProfile?.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          transformResponse: (data) => {
            const rawRows = data?.tableData || [];
            const exportRows = rawRows.map((row) => {
              const normalized = normalizeHistoryRow(row);
              return {
                projectId: normalized.projectId || '',
                projectName: normalized.projectName || '',
                clientName: normalized.clientName || '',
                customerName: normalized.customerName || '',
                projectVertical: normalized.projectVertical || '',
                projectType: normalized.projectType || '',
                projectStatus: normalized.projectStatus || '',
                projectManager: normalized.projectManager || '',
                baselineStartDate: formatDisplayDate(normalized.baselineStartDate),
                baselineEndDate: formatDisplayDate(normalized.baselineEndDate),
                actualStartDate: formatDisplayDate(normalized.actualStartDate),
                replannedEndDate: formatDisplayDate(normalized.replannedEndDate),
                activeDate: formatDisplayDate(normalized.activeDate),
                receivedDate: formatDisplayDate(normalized.receivedDate),
                csatNotReceivedDate: formatDisplayDate(normalized.csatNotReceivedDate),
                serviceQuality: typeof normalized.serviceQuality === 'number' ? normalized.serviceQuality.toFixed(2) : '0.00',
                roi: typeof normalized.roi === 'number' ? normalized.roi.toFixed(2) : '0.00',
                management: typeof normalized.management === 'number' ? normalized.management.toFixed(2) : '0.00',
                overall: typeof normalized.overall === 'number' ? normalized.overall.toFixed(2) : '0.00',
                feedback: normalized.feedback || '',
              };
            });

            const exportColumnsConfig = [
              { header: 'Project ID', field: 'projectId', width: 15 },
              { header: 'Project Name', field: 'projectName', width: 30 },
              { header: 'Project Vertical', field: 'projectVertical', width: 20 },
              { header: 'Project Type', field: 'projectType', width: 20 },
              { header: 'Project Status', field: 'projectStatus', width: 15 },
              { header: 'Project Manager', field: 'projectManager', width: 25 },
              { header: 'Baseline Start Date', field: 'baselineStartDate', width: 18 },
              { header: 'Baseline End Date', field: 'baselineEndDate', width: 18 },
              { header: 'Actual Start Date', field: 'actualStartDate', width: 18 },
              { header: 'Replanned End Date', field: 'replannedEndDate', width: 18 },
              { header: 'CSAT Received Date', field: 'receivedDate', width: 18 },
              { header: 'Quality of Service from Niveus', field: 'serviceQuality', width: 28 },
              { header: 'Return-On-Investment on Project', field: 'roi', width: 28 },
              { header: 'Project Management & Communication on Project', field: 'management', width: 40 },
              { header: 'Overall Experience', field: 'overall', width: 18 },
              { header: 'Customer Name', field: 'customerName', width: 25 },
              { header: 'Other Feedback', field: 'feedback', width: 35 },
            ];

            return {
              tableConfiguration: exportColumnsConfig,
              tableData: exportRows,
            };
          },
          filename: () => {
            const now = new Date();
            const suffix = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;
            return `Project_csat-${suffix}`;
          },
          sheetName: () => {
            const now = new Date();
            const suffix = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;
            const name = `Project_csat-${suffix}`;
            return name.substring(0, 31);
          },
          reportTitle: () => 'CSAT History Report',
        },
      },
      handler: {
        handleFilteredApiRequest,
        handleFilteredApiResponse,
        setTableStateHandler: (stateValue) => {
          const resolvedState = typeof stateValue === 'function' ? stateValue() : stateValue;
          if (resolvedState && typeof resolvedState === 'object') {
            tableStateRef.current = resolvedState;
          }
          if (resolvedState?.pagination) {
            const nextPage = Number(resolvedState.pagination.page) || 1;
            const nextLimit = Number(resolvedState.pagination.limit) || 10;
            currentPageRef.current = nextPage;
            setPagination((prev) => {
              if (prev.page === nextPage && prev.limit === nextLimit) return prev;
              return { ...prev, page: nextPage, limit: nextLimit };
            });
          }
        },
      },
      headers: { token: userProfile?.access_token || '' },
      showPagination: true,
      showToolBar: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: canUpdateCsat || canDeleteCsat,
        showDownloadCsv: false,
        showColumnCustomize: false,
        showColumnFilterMenu: true,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'CSAT History',
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
        additionalElements: toolbarAdditionalElements,
      },
    },
    showPagination: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      tableConfig: csatHistoryConfig,
      pageSize: 10,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: csatHistoryConfig.length },
      paginationConfig: {
        // Use currentPageRef.current (not pagination state) so config rebuilds
        // never snap the table back to page 1 – identical to EmployeeCostDetailsMain.
        page: currentPageRef.current,
        limit: 10,
        total: __pagination.total,
        onPaginationChange: handlePaginationChange,
        rowsPerPageOptions: [10, 15, 25, 50],
        showRowsPerPage: true,
      },
      showExpandCollapse: false,
      tableScrollHeight: '70vh',
      maxFrozenColumns: { desktop: 0, tablet: 0, mobile: 0 },
    },
  }), [toolbarAdditionalElements, userProfile, canUpdateCsat, canDeleteCsat, __pagination.total]);

  const tableKey = `csat-history-table-${tableRefreshNonce}`;

  // Scopes the table to the project the user navigated from (Project Details → CSAT MIS).
  // `handler.handleFilteredApiRequest` is NOT wired into the table-utility library's actual
  // axios call, so projectId must be injected via the supported `externalFilters` prop instead.
  const externalFilters = useMemo(() => {
    if (!projectId) return [];
    return [
      {
        field: 'projectId',
        selectedValue: String(projectId),
        selectedValues: [String(projectId)],
      },
    ];
  }, [projectId]);

  if (!canViewCsat) {
    return <Navigate to="/home" replace />;
  }

  if (isMisRoute && !canViewMisCsat) {
    return <Navigate to="/delivery/project-details" replace />;
  }

  return (
    <div className="csat-history-page-wrapper">
      <div className="csat-history-table-container">
        <TableUtility
          key={tableKey}
          configuration={configuration}
          externalFilters={externalFilters}
          showFilterOption
        />
      </div>

      <LocalCSATEditModal
        isOpen={modalState.csatEdit.isOpen}
        data={modalState.csatEdit.data}
        customers={activeEditProjectCustomers}
        isCustomersLoading={isModalCustomersLoading}
        customersError={null}
        onClose={handleEditClose}
        onSubmit={submitCSATEdit}
        isSubmitting={isActionInProgress}
      />

      <CsatDeleteModal
        isOpen={modalState.csatDelete.isOpen}
        onClose={handleDeleteClose}
        onConfirm={confirmCSATDelete}
        isSubmitting={isActionInProgress}
      />
    </div>
  );
};

export default CsatHistory;
